import Big from 'big.js';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { BillLimitDto, InhouseDigitalBillDetailsDto } from '@ordr-as/ordr-service-types/epos';
import { Checkbox } from '@ordr-as/umami-react/checkbox';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { PhoneInput, TextInput } from '@ordr-as/umami-react/form';
import { useLocation } from '../../../../../../context/LocationContext';
import { useScreenSize } from '../../../../../../context/ScreenSizeContext';
import { useUpdateBillLimit } from '../../../../../../hooks/useDigitalBillApi';
import { isProblemDetail } from '../../../../../../utils/problemDetail';
import { isValidPhoneNumber, splitPhoneNumber } from '../../../../../../utils/phoneNumber';

interface EditBillLimitDialogProps {
  digitalBill: InhouseDigitalBillDetailsDto;
  billLimit: BillLimitDto;
  onClose: () => void;
}

const EditBillLimitDialog = ({ digitalBill, billLimit, onClose }: EditBillLimitDialogProps) => {
  const intl = useIntl();
  const { isPhone } = useScreenSize();
  const { location, formatCurrencyWithoutDecimals } = useLocation();
  const { mutate, isPending, isError, error } = useUpdateBillLimit(location.locationId, digitalBill.reference);

  const prefill = splitPhoneNumber(digitalBill.mainContactDetails?.phone);
  const currentMax = billLimit.maxAmount ?? 0;

  const [amount, setAmount] = useState('');
  const [verifyWithSms, setVerifyWithSms] = useState(false);
  // PhoneInput only reports changes, so start from the prefilled number
  const [phoneNumber, setPhoneNumber] = useState(
    prefill.dialCode ? `${prefill.dialCode}${prefill.nationalNumber}` : ''
  );

  const consumedAmount = billLimit.consumedAmount ?? 0;
  const parsedAmount = Number.parseFloat(amount) || 0;
  const newMax = parsedAmount > 0 ? new Big(parsedAmount).round(2).toNumber() : 0;
  const changeBy = newMax > 0 ? new Big(newMax).minus(currentMax).toNumber() : 0;

  // Decreasing is fine down to what is already ordered; below that the bill would be
  // instantly over max, so block it client-side (the server stays permissive, R18)
  const isBelowConsumed = newMax > 0 && newMax < consumedAmount;
  const isAmountValid = newMax > 0 && !isBelowConsumed;
  const isPhoneValid = !verifyWithSms || isValidPhoneNumber(phoneNumber);
  const showPhoneError = verifyWithSms && phoneNumber.trim().length > 0 && !isPhoneValid;
  const canConfirm = isAmountValid && isPhoneValid && !isPending;

  const errorMessage =
    isProblemDetail(error) && error.detail
      ? error.detail
      : intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.error' });

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }

    mutate(
      {
        data: {
          maxAmount: newMax,
          phoneNumber: verifyWithSms ? phoneNumber : undefined,
          verifyWithSms,
        },
        locationId: location.id,
        reference: digitalBill.reference,
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog
      onClose={onClose}
      title={intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.title' })}
      onAction={handleConfirm}
      actionLabel={intl.formatMessage({
        id: verifyWithSms ? 'digitalBill.billLimit.editDialog.sendSms' : 'digitalBill.billLimit.editDialog.confirm',
      })}
      isDisabledAction={!canConfirm}
      size={isPhone ? 'fullwidth' : 'default'}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 rounded bg-grey-lightest p-4">
          <span className="text-sm">
            {intl.formatMessage(
              { id: 'digitalBill.billLimit.editDialog.currentMax' },
              { amount: formatCurrencyWithoutDecimals(currentMax) }
            )}
          </span>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-grey-dark">
              {intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.newLimitLabel' })}
            </span>
            <div className={`flex gap-4 ${isPhone ? 'flex-col' : 'flex-row items-center'}`}>
              <div className="flex-1">
                <TextInput
                  type="number"
                  value={amount}
                  onChange={setAmount}
                  data-cy="new-bill-limit"
                  isDisabled={isPending}
                  fullWidth
                  validationState={isBelowConsumed ? 'invalid' : undefined}
                  invalidHelpText={intl.formatMessage(
                    { id: 'digitalBill.billLimit.editDialog.belowConsumed' },
                    { amount: formatCurrencyWithoutDecimals(consumedAmount) }
                  )}
                />
              </div>
              {(!isPhone || changeBy !== 0) && (
                <span className="flex-1 text-sm ">
                  {changeBy > 0 &&
                    intl.formatMessage(
                      { id: 'digitalBill.billLimit.editDialog.increaseBy' },
                      { amount: formatCurrencyWithoutDecimals(changeBy) }
                    )}
                  {changeBy < 0 &&
                    !isBelowConsumed &&
                    intl.formatMessage(
                      { id: 'digitalBill.billLimit.editDialog.decreaseBy' },
                      { amount: formatCurrencyWithoutDecimals(Math.abs(changeBy)) }
                    )}
                </span>
              )}
            </div>
          </div>
        </div>
        <Checkbox isSelected={verifyWithSms} onChange={setVerifyWithSms} isDisabled={isPending}>
          <span className="flex flex-col">
            <span className="font-bold">
              {intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.verifyWithSms' })}
            </span>
            <span className="text-sm text-grey-dark">
              {intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.verifyWithSmsDescription' })}
            </span>
          </span>
        </Checkbox>
        {verifyWithSms && (
          <div>
            <PhoneInput
              label={intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.phoneLabel' })}
              fullWidth
              isDisabled={isPending}
              defaultCountryDialCode={prefill.dialCode}
              defaultPhoneNumber={prefill.nationalNumber}
              sendPhoneNumberToParent={setPhoneNumber}
              validationState={showPhoneError ? 'invalid' : undefined}
              invalidHelpText={intl.formatMessage({ id: 'digitalBill.billLimit.editDialog.phoneInvalid' })}
              className="mb-[120px]"
            />
          </div>
        )}
        {isError && <p className="text-sm text-orange-darkest">{errorMessage}</p>}
      </div>
    </Dialog>
  );
};

export default EditBillLimitDialog;
